import {
  AudienceIcon,
  EditIcon,
  IconButtonColorEnum,
  IconButtonSection,
  IconButtonSectionInternalProps,
  IconButtonSectionSizeEnum,
  IconCounter,
  ImageThumbnail,
  ImageThumbnailSizesEnum,
  TextOrientationEnum,
  Tooltip,
  Typography,
  ValorationIcon,
} from '@acceso/design-system';
import { useTranslation } from 'react-i18next';

import { CopyTextButton } from '@/components/CopyTextButton/CopyTextButton';
import { ItemList } from '@/components/ItemList';
import { RenderEnrichementsIcons } from '@/components/RenderEnrichementsIcons/RenderEnrichementsIcons';
import { StateActiveInTable } from '@/components/StateActiveInTable/StateActiveInTable';
import { UserAction } from '@/components/UserAction';
import { useAudienceChannelSources } from '@/features/broadcasters/api/get-audience-channel-sources';
import { useChannels } from '@/features/broadcasters/api/get-channels';
import { useEconomicalValueChannelSources } from '@/features/broadcasters/api/get-economical-value-channel-sources';
import { CHANNEL_TYPE_TO_TRANSLATION_KEY } from '@/features/broadcasters/constants';
import { CHANNEL_MEDIA_TYPE_TO_ICON } from '@/features/broadcasters/constants/icons';
import { ChannelType, ChannelTypeEnum } from '@/features/broadcasters/types';
import { useLocationCodeToLabel } from '@/features/locations/hooks/useGetLocationLabelFromCode';

const internalPropsIconButtonEditUser: IconButtonSectionInternalProps = {
  icon: {
    type: EditIcon,
  },
  iconButton: {
    color: IconButtonColorEnum.NONE_DEFAULT,
  },
  text: {
    value: 'Editar',
    orientation: TextOrientationEnum.LEFT,
    className: 'text-text-dark-10 text-body-3 font-regular',
  },
};

type BroadcastersDetailsProps = {
  broadcasters: ChannelType;
  onUpdate: (broadcasters: ChannelType) => void;
};

export function BroadcastersDetail({ broadcasters, onUpdate }: BroadcastersDetailsProps) {
  const { t } = useTranslation();
  const { data: { data: channels = [] } = {} } = useChannels();
  const ChannelMediaTypeIcon = CHANNEL_MEDIA_TYPE_TO_ICON[broadcasters.media_type];
  const parentChannel = broadcasters.parent_channel_id
    ? channels.find(channel => channel.id === broadcasters.parent_channel_id)
    : null;
  const locationCodeToLabel = useLocationCodeToLabel();
  const economicalValueId = broadcasters.economical_value_channel_source_id || null;
  const { data: economicalValueChannelSources = [] } = useEconomicalValueChannelSources({
    params: economicalValueId ? { idIn: [economicalValueId] } : undefined,
    config: { enabled: Boolean(economicalValueId) },
  });
  const audienceId = broadcasters.audience_channel_source_id || null;
  const { data: audienceChannelSources = [] } = useAudienceChannelSources({
    params: audienceId ? { idIn: [audienceId] } : undefined,
    config: { enabled: Boolean(audienceId) },
  });

  const audienceChannelSourceId = audienceChannelSources.find(
    audienceChannelSource => audienceChannelSource.id === broadcasters.audience_channel_source_id
  );
  const economicalValueChannelSourceId = economicalValueChannelSources.find(
    economicalValueChannelSource =>
      economicalValueChannelSource.id === broadcasters.economical_value_channel_source_id
  );

  return (
    <div className="flex flex-1 flex-col items-start self-stretch">
      <div className="flex items-start justify-end self-stretch">
        <IconButtonSection
          onClick={() => onUpdate(broadcasters)}
          internalProps={internalPropsIconButtonEditUser}
          size={IconButtonSectionSizeEnum.LG}
        />
      </div>
      <div className="flex w-full flex-col divide-y divide-stroke-20">
        <ItemList title={t('Logo')}>
          <ImageThumbnail
            src={broadcasters.parameters?.logo_presigned_url}
            size={ImageThumbnailSizesEnum.Xxl}
          />
        </ItemList>
        <ItemList title={t('Nombre corto')} contentIcon={ChannelMediaTypeIcon}>
          {broadcasters.name}
        </ItemList>
        <ItemList title={t('ID AV (Legacy)')}> {broadcasters.av_code} </ItemList>
        <ItemList title={t('Emisora madre')}>
          <div className="flex flex-col gap-1">
            <Typography className="font-[500] text-text-dark-10">{parentChannel?.name}</Typography>
            <Typography className="text-text-medium">{parentChannel?.short_name}</Typography>
          </div>
        </ItemList>
        <ItemList title={t('Tipo')}>
          {t(
            CHANNEL_TYPE_TO_TRANSLATION_KEY[
              broadcasters.type ?? ChannelTypeEnum.TERRITORIAL_DISCONNECTION
            ]
          )}
        </ItemList>
        <ItemList title={t('Estado')}>
          <StateActiveInTable status={broadcasters.state} />
        </ItemList>
        <ItemList title={t('IDs Archivos')}>
          {audienceChannelSourceId && (
            <Tooltip help={t('Audiencias')} grow>
              <IconCounter icon={AudienceIcon} count={audienceChannelSourceId.name} />
            </Tooltip>
          )}

          {economicalValueChannelSourceId && (
            <Tooltip help={t('Tarifas')} grow>
              <IconCounter icon={ValorationIcon} count={economicalValueChannelSourceId.name} />
            </Tooltip>
          )}
        </ItemList>
        <ItemList title={t('Ámbito')}> {broadcasters.scope} </ItemList>
        <ItemList title={t('País')}> {locationCodeToLabel[broadcasters.country_code]} </ItemList>
        <ItemList title={t('Región')}>
          {broadcasters.region_code && locationCodeToLabel[broadcasters.region_code]}
        </ItemList>
        <ItemList title={t('Subregión')}>
          {broadcasters.subregion_code && locationCodeToLabel[broadcasters.subregion_code]}
        </ItemList>
        <ItemList title={t('Enriquecimiento')}>
          <RenderEnrichementsIcons
            enrichments={{
              ocr: broadcasters.parameters?.enrichments?.ocr,
              stt: broadcasters.parameters?.enrichments?.stt,
              logos: broadcasters.parameters?.enrichments?.logos,
              peoples: broadcasters.parameters?.enrichments?.peoples,
              advertising: broadcasters.parameters?.enrichments?.advertising,
            }}
          />
        </ItemList>
        <ItemList title={t('URL de stream')}>
          <div className="flex items-center gap-2">
            {broadcasters.uri}

            {broadcasters.uri && (
              <CopyTextButton content={broadcasters.uri} message="URL de stream copiada" />
            )}
          </div>
        </ItemList>
        <ItemList title={t('Actualizado por')}>
          <UserAction
            id={broadcasters?.created_by || new Date().toISOString()}
            timestamp={broadcasters?.created_at || new Date().toISOString()}
          />
        </ItemList>
        <ItemList title={t('Creado por')}>
          <UserAction
            id={broadcasters?.created_by || new Date().toISOString()}
            timestamp={broadcasters?.created_at || new Date().toISOString()}
          />
        </ItemList>
      </div>
    </div>
  );
}
